/**
 * Brand Bible page sub-nav: workspace header, completeness (v2 score or the legacy ring),
 * the A-I section list and the save status. A pure render component moved out of
 * BrandBiblePage.tsx (3,000-line rule) with no behaviour change: every value it shows is
 * passed in from the page, exactly as the inline JSX read it.
 */
import { BookOpen, Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { BRAND_BIBLE_SECTIONS as SECTIONS } from '@/components/brand-bible/BrandBibleSectionNavigation';
import { ProgressRing } from '@/components/brand-bible/SectionHeader';
import { ScoreChip } from '@/components/brand-bible-wizard/primitives';
import {
  fmtSavedAt,
  sectionStatus,
  type BrandBibleDraft,
  type SectionStatusV2Context,
} from '@/pages/brand-bible/draftModel';
import type { BibleScore } from '@/features/brand-bible-wizard/types';
import { BB_LETTER_TO_SECTION, type BbLetter, type SectionId, type sectionRollups } from '@/lib/bbSectionMap';

interface BrandBibleSubNavProps {
  /** Workspace label under the title (tenant website, else the tenant id). */
  tenantLabel: string;
  v2Score: BibleScore | null;
  v2Delta: number | null;
  /** Rows left from the current file drop; 0 when no drop review is active. */
  dropRemaining: number;
  /** toVerifyRows + draftRows: everything waiting for a person. */
  attentionTotal: number;
  /** Legacy 7-section completeness (fallback ring for tenants not on v2). */
  pct: number;
  completedCount: number;
  legacyCount: number;
  v2Rollups: ReturnType<typeof sectionRollups> | null;
  draft: BrandBibleDraft;
  v2ServiceSectionContext: SectionStatusV2Context;
  active: SectionId;
  v2Attention: Record<SectionId, number>;
  onSelectLetter: (letter: BbLetter) => void;
  dirtyLetters: BbLetter[];
  onJump: (section: SectionId) => void;
  onSaveAll: () => void | Promise<void>;
  savingAll: boolean;
  /** update or create mutation in flight. */
  isSaving: boolean;
  isLoading: boolean;
  savedAt: Date | null;
}

export function BrandBibleSubNav({
  tenantLabel,
  v2Score,
  v2Delta,
  dropRemaining,
  attentionTotal,
  pct,
  completedCount,
  legacyCount,
  v2Rollups,
  draft,
  v2ServiceSectionContext,
  active,
  v2Attention,
  onSelectLetter,
  dirtyLetters,
  onJump,
  onSaveAll,
  savingAll,
  isSaving,
  isLoading,
  savedAt,
}: BrandBibleSubNavProps) {
  return (
      <aside className="bg-warm-bg border-r border-border flex flex-col p-3">
        <div className="flex items-center gap-2.5 px-2 pt-1 pb-3.5 border-b border-border mb-3.5">
          <div className="w-[30px] h-[30px] rounded-md bg-coral text-white grid place-items-center">
            <BookOpen className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[13.5px] font-bold -tracking-[0.005em]">Brand Bible</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {tenantLabel}
            </div>
          </div>
        </div>

        {/* Completeness (design spec item 1): v2 score once the tenant has any
            v2 data (contract §4.1 — one number, one owner); the legacy 7-section
            ring is the fallback for tenants that haven't started v2 yet, so
            nothing regresses for them. */}
        {v2Score ? (
          <div className="px-2 pb-2.5 flex flex-col gap-2">
            <ScoreChip score={v2Score} delta={v2Delta} />
            {(dropRemaining > 0 ? dropRemaining : attentionTotal) > 0 && (
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[hsl(var(--status-warning-bg))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--status-warning))]">
                {dropRemaining > 0
                  ? `${dropRemaining} left from this drop`
                  : `${attentionTotal} need attention`}
              </span>
            )}
          </div>
        ) : (
          <div className="px-2 pb-2.5 flex items-center gap-2.5">
            <ProgressRing pct={pct} />
            <div>
              <div className="text-[13px] font-semibold leading-none">{pct}% ready</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {completedCount} of {legacyCount} sections
              </div>
            </div>
          </div>
        )}

        {/* One nav, not two. The old letter strip used to sit above a numbered list, so
            every section had two identities and an active section lit up twice
            (letter B and row 03). The spec only ever had letters — this list IS
            the strip now. */}
        <nav aria-label="Brand Bible sections" className="flex flex-col gap-px mt-1.5">
          {SECTIONS.map((s) => {
            const roll = v2Rollups?.[s.id];
            // One truth per rail: once the tenant is on v2, the row state comes
            // from the same score the ring shows. The legacy flat-row heuristic
            // (any field filled = ✓) contradicted it — a 1-of-12-points section
            // still rendered "done" beside a low score ring.
            const done = roll
              ? roll.max > 0 && roll.points >= roll.max
              : sectionStatus(s.id, draft, v2ServiceSectionContext);
            const started = roll ? roll.points > 0 : done;
            const isActive = active === s.id;
            const attention = v2Attention?.[s.id] ?? 0;
            return (
              <button
                key={s.id}
                type="button"
                aria-current={isActive ? 'page' : undefined}
                aria-label={`${s.letter} · ${s.title}`}
                onClick={() => onSelectLetter(s.letter)}
                className={cn(
                  'flex items-center gap-2.5 px-2.5 py-2 border-none rounded-md text-left cursor-pointer relative transition-colors',
                  isActive
                    ? 'bg-card text-foreground shadow-sm font-semibold'
                    : 'bg-transparent text-muted-foreground hover:bg-black/[0.03]',
                )}
              >
                <span
                  className={cn(
                    'grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded font-mono text-[10px] font-semibold',
                    isActive ? 'bg-coral text-white' : 'bg-warm-bg text-muted-foreground',
                  )}
                >
                  {s.letter}
                </span>
                <span className="flex-1 min-w-0">
                  <span className={cn('block text-[13px]', !isActive && 'font-medium')}>{s.title}</span>
                  {roll && roll.max > 0 && (
                    <span className="block text-[10.5px] tabular-nums text-muted-foreground">
                      {roll.points} of {roll.max} pts
                      {attention > 0 && ` · ${attention} to check`}
                    </span>
                  )}
                </span>
                {done ? (
                  <span
                    title="Section complete"
                    className="grid place-items-center w-4 h-4 rounded bg-teal-light text-teal-dark flex-shrink-0"
                  >
                    <Check className="size-3" />
                  </span>
                ) : started ? (
                  <span
                    title="Points still available here"
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[hsl(var(--status-warning))]"
                  />
                ) : (
                  <span title="Nothing entered yet" className="w-1.5 h-1.5 rounded-full bg-border flex-shrink-0" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex-1" />

        {dirtyLetters.length > 0 ? (
          // Standing Unsaved indicator (save-ux slate #2): state-aware pill —
          // amber, letters clickable (jump to that section), one-tap Save all.
          <div
            data-testid="bb-unsaved-indicator"
            className="px-3 py-2.5 rounded-lg bg-[hsl(var(--status-warning)/0.08)] border border-[hsl(var(--status-warning)/0.4)] flex items-center gap-2 text-[12px]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-warning))] flex-shrink-0" />
            <span className="font-semibold text-foreground flex-shrink-0">Unsaved:</span>
            <span className="flex items-center gap-1 min-w-0">
              {dirtyLetters.map((l, i) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => onJump(BB_LETTER_TO_SECTION[l])}
                  className="font-mono font-bold text-foreground hover:text-coral cursor-pointer"
                  title={`Go to section ${l}`}
                >
                  {l}
                  {i < dirtyLetters.length - 1 ? ' ·' : ''}
                </button>
              ))}
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={onSaveAll}
              disabled={savingAll}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-coral hover:bg-coral-hover text-white text-[11.5px] font-semibold cursor-pointer flex-shrink-0"
            >
              {savingAll && <Loader2 className="size-3 animate-spin" />}
              Save all
            </button>
          </div>
        ) : (
          <div className="px-3 py-2.5 rounded-lg bg-card border border-border flex items-center gap-2 text-[12px] text-muted-foreground">
            <span className="grid place-items-center w-4 h-4 rounded bg-teal-light text-teal-dark">
              {isSaving ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Check className="size-3" />
              )}
            </span>
            <span className="truncate">
              {isSaving
                ? 'Saving…'
                : isLoading
                ? 'Loading…'
                : savedAt
                ? `Auto-saved · ${fmtSavedAt(savedAt)}`
                // Nothing has been saved this session — "Auto-saved · —" claimed a
                // save that never happened; state the capability instead.
                : 'Auto-save on'}
            </span>
          </div>
        )}
      </aside>
  );
}
